// Query Keys
// Defines all query keys used in React Query for caching and invalidation

export const QUERY_KEYS = {
  // User Keys
  USERS: 'users',
  USER_DETAIL: 'userDetail',
  CURRENT_USER: 'currentUser',
  USER_PROFILE: 'userProfile',
  USER_DEMOGRAPHICS: 'userDemographics',

  // Whereby Keys
  LIVE_WHEREBY_ROOMS: 'liveWherebyRooms',
  TEAM_WHEREBY_MEETING: 'teamWherebyMeeting',
  ACTIVITY_WHEREBY_MEETING: 'activityWherebyMeeting',
  SPRINT_ACTIVITY_WHEREBY_MEETING: 'sprintActivityWherebyMeeting',

  // Session Keys
  SESSIONS: 'sessions',
  SESSION_DETAIL: 'sessionDetail',
  UPCOMING_SESSIONS: 'upcomingSessions',
  PAST_SESSIONS: 'pastSessions',
  ACTIVE_SESSIONS: 'activeSessions',
  SESSIONS_BY_FACILITATOR: 'sessionsByFacilitator',
  SESSIONS_BY_PARTICIPANT: 'sessionsByParticipant',

  // Team Keys
  TEAMS: 'teams',
  TEAM_DETAIL_PREFIX: 'teamDetail',
  REPORT_DETAIL_PREFIX: 'reportDetail',
  REFLECTIONS_PREFIX: 'reflections',
  REFLECTION_DETAIL_SEGMENT: 'detail',
  TEAM_DETAIL: (activeOrganizationId: string | undefined, id: string) =>
    ['teamDetail', activeOrganizationId, id] as const,
  TEAMS_BY_IDS: 'teamsByIds',
  TEAMS_BY_MEMBER: (activeOrganizationId: string | undefined, memberId: string) =>
    ['teamsByMember', activeOrganizationId, memberId] as const,
  TEAMS_BY_FACILITATOR: 'teamsByFacilitator',
  ACTIVE_TEAMS: 'activeTeams',
  TEAM_STATS: 'teamStats',

  // Group Keys
  GROUPS: 'groups',
  GROUP_DETAIL: 'groupDetail',
  GROUPS_WITH_TEAMS: 'groupsWithTeams',
  ACTIVE_GROUPS: 'activeGroups',

  // Report Keys
  REPORTS: 'reports',
  REPORT_DETAIL: (activeOrganizationId: string | undefined, id: string) =>
    ['reportDetail', activeOrganizationId, id] as const,
  REPORTS_BY_SESSION: 'reportsBySession',
  REPORTS_BY_TEAM: 'reportsByTeam',
  REPORTS_BY_USER: 'reportsByUser',
  GENERATED_REPORTS: 'generatedReports',

  // Dashboard Keys
  FACILITATOR_DASHBOARD: 'facilitatorDashboard',
  FACILITATOR_LIVE_SESSION_MONITOR: 'facilitatorLiveSessionMonitor',

  // Training Sprint Keys
  TRAINING_SPRINTS: 'trainingSprints',
  TRAINING_SPRINT_DETAIL: 'trainingSprintDetail',
  ACTIVE_TRAINING_SPRINTS: 'activeTrainingSprints',
  UPCOMING_TRAINING_SPRINTS: 'upcomingTrainingSprints',

  // Sprint Keys (Firebase Backend)
  SPRINT: 'sprint',
  SPRINTS: 'sprints',

  // Search Keys
  GLOBAL_SEARCH: 'globalSearch',
  USER_SEARCH: 'userSearch',
  SESSION_SEARCH: 'sessionSearch',

  // Configuration Keys
  APP_CONFIG: 'appConfig',
  USER_CONFIG: 'userConfig',

  // Additional Keys for QueryHooksDeprecated migration
  SLOT_USERS: 'slotUsers',
  SESSION_FOR_TEAM: 'sessionForTeam',
  SESSION_SLOTS: 'sessionSlots',
  SESSIONS_FOR_TEAM: 'sessionsForTeam',
  QUESTIONNAIRE: 'questionnaire',
  QUESTIONNAIRE_ANSWER: 'questionnaireAnswer',

  // Report Generation Keys
  SESSIONS_FOR_SPRINT_TEAM: 'sessions-for-sprint-team',
  SESSIONS_DATAPOINTS: 'sessions-datapoints',

  // Activity Keys
  UPCOMING_ACTIVITIES: 'upcoming-activities',
  UPCOMING_ACTIVITIES_GAMER: 'upcoming-activities-gamer', // Single activity with sessions
  UPCOMING_ACTIVITIES_GAMER_LIST: 'upcoming-activities-gamer-list', // List of activities

  // Feedback Keys
  LATEST_SESSION_FEEDBACK: 'latestSessionFeedback',
  TEAM_SWIMMER_FEEDBACK: 'teamSwimmerFeedback',
  SKIMMER_DATA_FOR_GAMER: 'skimmerDataForGamer',
  SWIMMER_TEAM_DATA_FOR_GAMER: 'swimmerTeamDataForGamer',
  SWIMMER_INDIVIDUAL_DATA_FOR_GAMER: 'swimmerIndividualDataForGamer',

  // Achievement Keys
  ACHIEVEMENTS: 'achievements',
  USER_ACHIEVEMENTS: 'userAchievements',

  // Feedback Readiness Keys
  FEEDBACK_READINESS: 'feedbackReadiness',

  // AI Coach Keys
  AI_COACH_SESSION: 'aiCoachSession',

  // One-on-One Coach (Aria) Keys
  COACH_STATUS: 'coachStatus',
  COACH_CONVERSATIONS: 'coachConversations',
  COACH_CONVERSATION_DETAIL: (id: string) => ['coachConversation', id] as const,
  USER_GOALS: 'userGoals',

  // Leader Keys
  LEADER_SCOPE: (organizationId: string | undefined, leaderUserId: string | undefined) =>
    ['leaderScope', organizationId, leaderUserId] as const,
  LEADER_INSIGHTS: (organizationId: string | undefined, leaderUserId: string | undefined) =>
    ['leaderInsights', organizationId, leaderUserId] as const,

  // User Profile Report Keys
  USER_PROFILE_REPORT: 'userProfileReport',

  // Translation Keys
  TRANSLATIONS: 'translations',
  TRANSLATION_DETAIL: 'translationDetail',

  // Video Keys
  EXPERT_VIDEOS: 'expertVideos',
  TUTORIAL_VIDEOS: 'tutorialVideos',

  // Blog Post Keys
  BLOG_POSTS: 'blogPosts',

  // Quote Keys
  QUOTES: 'quotes',

  // Landing config (quotes section on homepage)
  LANDING_QUOTES_SECTION_ENABLED: 'landingQuotesSectionEnabled',

  // Sprint Wizard Config Keys
  SPRINT_PURPOSE_CONFIGS: 'sprintPurposeConfigs',
  SPRINT_ACTIVITY_TYPE_CONFIGS: 'sprintActivityTypeConfigs',

  // In-App Notification Keys
  IN_APP_NOTIFICATIONS: 'inAppNotifications',

  // User Template Keys
  USER_TEMPLATES: 'userTemplates', // selector key — mutations invalidate CURRENT_USER

  // Reflection Keys (scoped by organizationId so org switch refetches lists + detail cache)
  REFLECTIONS: (organizationId: string | undefined, status?: string) =>
    ['reflections', organizationId, status ?? 'all'] as const,
  REFLECTION_DETAIL: (organizationId: string | undefined, reflectionId: string) =>
    ['reflections', 'detail', organizationId, reflectionId] as const,
  REFLECTION_SCOPE_DATA: 'reflection-scope-data',

  /** Super-admin org directory (`getOrganizations`). */
  ORGANIZATIONS_DIRECTORY: 'organizationsDirectory',
  ORGANIZATION_DETAIL: (organizationId: string) => ['organization', 'detail', organizationId] as const,
  ORG_MEMBERS: (organizationId: string) => ['organization', 'members', organizationId] as const,
  ORGANIZATION_INVITES: (organizationId: string) => ['organization', 'invites', organizationId] as const
} as const

export type QueryKey = keyof typeof QUERY_KEYS
